import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shopConfig as cfg } from '../shopConfig';
import { useBrand } from '../hooks/useBrand';
import { useCart } from '../hooks/useCart';

// -----------------------------------------------------------------------------
// ProductPage.tsx - the product detail view.
//
// Reads smartshop-get-product?slug=&gido_id=. Shows an image gallery (the
// migrated images - main + thumbnails, using the {medium} rendition where the
// backend provides it and {small} for thumbs), the price, the variant selector
// (D-048: sizes/colours; selecting a variant updates price/stock/image), and an
// add-to-cart action. Mobile-first: single column stacked on phones, two-column
// (gallery | details) on desktop.
//
// D-048 variant rules honoured: variant price is ABSOLUTE (price_cents; null =
// inherit product price); a variant may carry its own image (else product
// primary); out-of-stock variant blocks add. Options[] are informational only.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------

const money = (cents: number | null, ccy = 'EUR') =>
  cents == null ? '' : new Intl.NumberFormat('en-IE', { style: 'currency', currency: ccy }).format(cents / 100);

interface Variant {
  variant_id: string;
  name: string;
  price_cents: number | null;
  stock_status: string;
  image: string | null;
}
interface Product {
  gid: string; product_id: string; name: string; slug: string; category: string;
  price: number | null; price_cents: number | null; currency: string; price_type: string;
  primary_image: string; secondary_image?: string; all_assets: string[];
  stock_status: string; description: string; technical_specs?: string;
  options?: any[]; variants: Variant[];
}

export const ProductPage: React.FC = () => {
  const { slug } = useParams();
  const brand = useBrand();
  const accent = brand.accent || cfg.brand.accent;
  const navigate = useNavigate();
  const { addToCart, adding } = useCart();

  const [p, setP] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeVariant, setActiveVariant] = useState<Variant | null>(null);
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const [hovering, setHovering] = useState(false);
  const cycleRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false); setP(null); setActiveVariant(null); setActiveImg(0);
    (async () => {
      try {
        const res = await fetch(`${cfg.api_base}/smartshop-get-product?slug=${encodeURIComponent(slug || '')}&gido_id=${cfg.gido_id}`);
        const data = await res.json();
        if (!alive) return;
        if (!data.product) { setNotFound(true); return; }
        setP(data.product);
      } catch {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // gallery images: variant image (if selected + has one) first, then product images
  const gallery = useMemo(() => {
    if (!p) return [];
    const imgs: string[] = [];
    if (activeVariant?.image) imgs.push(activeVariant.image);
    if (p.primary_image) imgs.push(p.primary_image);
    if (p.secondary_image && !imgs.includes(p.secondary_image)) imgs.push(p.secondary_image);
    (p.all_assets || []).forEach(a => { if (a && !imgs.includes(a)) imgs.push(a); });
    return imgs.length ? imgs : [];
  }, [p, activeVariant]);

    // SmartShop standard product-page cycle (copied from shop-0001, exact schema):
  // 3.5s zoom then flip, loops. Hovering STOPS it (holds current image).
  // transform 3.5s == interval 3500ms keeps zoom continuous (no dead gap).
  useEffect(() => {
    if (cycleRef.current) { clearInterval(cycleRef.current); cycleRef.current = null; }
    if (hovering || gallery.length < 2) { setZoomed(gallery.length >= 1); return; }
    setZoomed(false);
    const kick = setTimeout(() => setZoomed(true), 50);
    cycleRef.current = setInterval(() => {
      setZoomed(false);
      setTimeout(() => { setActiveImg(prev => (prev + 1) % gallery.length); setZoomed(true); }, 400);
    }, 3500);
    return () => { clearTimeout(kick); if (cycleRef.current) clearInterval(cycleRef.current); };
  }, [gallery.length, hovering]);

  // effective price/stock: variant overrides product (D-048)
  const effPriceCents = activeVariant?.price_cents ?? p?.price_cents ?? null;
  const effStock = activeVariant?.stock_status ?? p?.stock_status ?? 'in_stock';
  const soldOut = effStock === 'out_of_stock';
  const needsVariant = (p?.variants?.length ?? 0) > 0 && !activeVariant;

  const handleAdd = async () => {
    if (!p || soldOut || needsVariant) return;
    try {
      await addToCart(p.product_id, { qty: 1, variantId: activeVariant?.variant_id });
      setAdded(true);
      setTimeout(() => setAdded(false), 2200);
    } catch { /* useCart surfaces its own error */ }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'rgba(0,0,0,.4)' }}>Loading...</div>;
  if (notFound || !p) return (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <p style={{ fontSize: 18, color: cfg.brand.secondary, fontWeight: 700 }}>Product not found</p>
      <button onClick={() => navigate('/shop')} style={{ marginTop: 16, background: accent, color: cfg.brand.secondary, border: 'none', padding: '10px 20px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer' }}>Back to Shop</button>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '16px 16px 56px' }}>
      <style>{`
        .pd-wrap { display:flex; flex-direction:column; gap:20px }
        @media(min-width:860px){ .pd-wrap { flex-direction:row; gap:40px; align-items:flex-start } }
        .pd-gallery { flex:1; min-width:0 }
        .pd-main-img { width:100%; aspect-ratio:3/4; object-fit:cover; background:#f2efe6; display:block }
        .pd-thumbs { display:flex; gap:8px; margin-top:8px; overflow-x:auto; scrollbar-width:none }
        .pd-thumbs::-webkit-scrollbar { display:none }
        .pd-thumb { width:64px; height:82px; object-fit:cover; cursor:pointer; flex:0 0 auto; background:#f2efe6 }
        .pd-details { flex:1; min-width:0 }
        @media(min-width:860px){ .pd-details { position:sticky; top:150px } }
      `}</style>

      <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(0,0,0,.5)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.1em', cursor: 'pointer', padding: '4px 0 12px' }}>&larr; Back</button>

      <div className="pd-wrap">
        {/* gallery */}
        <div className="pd-gallery">
          {gallery.length
            ? <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', overflow: 'hidden', background: '#f2efe6', boxShadow: '0 2px 12px rgba(0,0,0,.12), 0 1px 4px rgba(0,0,0,.08)' }}
                onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
                {gallery.map((g, i) => (
                  <img key={i} className="pd-main-img" src={g} alt={p.name} loading={i === 0 ? 'eager' : 'lazy'} decoding="async"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                      opacity: activeImg === i ? 1 : 0,
                      transform: activeImg === i && zoomed ? 'scale(1.06)' : 'scale(1)',
                      transition: 'opacity 0.4s ease-in-out, transform 3.5s ease-in-out',
                      zIndex: activeImg === i ? 1 : 0 }} />
                ))}
              </div>
            : <div className="pd-main-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(0,0,0,.25)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.1em' }}>{cfg.shop_name}</div>}
          {gallery.length > 1 && (
            <div className="pd-thumbs">
              {gallery.map((g, i) => (
                <img key={i} className="pd-thumb" src={g} alt={`${p.name} ${i + 1}`}
                  onClick={() => { setHovering(true); setActiveImg(i); }}
                  style={{ outline: i === activeImg ? `2px solid ${accent}` : '1px solid rgba(0,0,0,.1)', outlineOffset: -1 }} />
              ))}
            </div>
          )}
        </div>

        {/* details */}
        <div className="pd-details">
          <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(0,0,0,.4)' }}>{p.category}</span>
          <h1 style={{ fontSize: 'clamp(20px,5vw,28px)', fontWeight: 900, fontStyle: 'italic', color: cfg.brand.secondary, margin: '4px 0 12px', lineHeight: 1.2 }}>{p.name}</h1>

          <div style={{ fontSize: 22, fontWeight: 800, color: accent, marginBottom: 16 }}>
            {p.price_type === 'variable' ? 'Gift Card' : money(effPriceCents, p.currency)}
          </div>

          {/* variant selector (D-048) */}
          {p.variants?.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <p style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(0,0,0,.5)', margin: '0 0 8px' }}>Select option</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {p.variants.map((v) => {
                  const vSoldOut = v.stock_status === 'out_of_stock';
                  const sel = activeVariant?.variant_id === v.variant_id;
                  return (
                    <button key={v.variant_id} disabled={vSoldOut}
                      onClick={() => { setActiveVariant(v); if (v.image) setActiveImg(0); }}
                      style={{
                        padding: '9px 15px', fontSize: 13, fontWeight: 700, cursor: vSoldOut ? 'not-allowed' : 'pointer',
                        border: sel ? `2px solid ${accent}` : '1px solid rgba(0,0,0,.25)',
                        background: sel ? `${accent}18` : '#fff', color: cfg.brand.secondary,
                        opacity: vSoldOut ? .4 : 1, textDecoration: vSoldOut ? 'line-through' : 'none',
                        transition: 'all .15s',
                      }}>
                      {v.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* add to cart */}
          <button onClick={handleAdd} disabled={soldOut || needsVariant || adding}
            style={{
              width: '100%', maxWidth: 340, padding: '15px', fontSize: 13, fontWeight: 800,
              textTransform: 'uppercase', letterSpacing: '.12em', cursor: (soldOut || needsVariant) ? 'not-allowed' : 'pointer',
              border: 'none', color: cfg.brand.secondary,
              background: (soldOut || needsVariant) ? 'rgba(0,0,0,.15)' : accent,
              transition: 'all .2s',
            }}>
            {soldOut ? 'Sold Out' : needsVariant ? 'Select an option' : added ? 'Added \u2713' : adding ? 'Adding...' : 'Add to Cart'}
          </button>

          {/* description */}
          {p.description && (
            <div style={{ marginTop: 26, paddingTop: 18, borderTop: '1px solid rgba(0,0,0,.08)' }}>
              <div style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(0,0,0,.75)' }}
                dangerouslySetInnerHTML={{ __html: p.description }} />
            </div>
          )}
          {p.technical_specs && (
            <div style={{ marginTop: 16, fontSize: 13, lineHeight: 1.6, color: 'rgba(0,0,0,.6)', whiteSpace: 'pre-wrap' }}>
              {p.technical_specs}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
